import {
  MAX_BUCKET_NAME_LENGTH,
  MAX_DOCUMENT_VALUE_BYTES,
  type SmartFillBucketOrigin,
  SMART_FILL_BUCKET_ORIGINS,
  type SmartFillBucketVisibility,
  SMART_FILL_BUCKET_VISIBILITIES
} from '../dtos';
import { isBridgeIdentifier, type BridgeRequestId, asBridgeRequestId } from '../protocol';

export type UnknownRecord = Record<string, unknown>;
export type Parser<T> = (value: unknown, path: string) => T;

export const MAX_COLLECTION_SIZE = 2_000;
const MAX_SHORT_TEXT = 256;
export const MAX_MESSAGE_TEXT = 2_048;
export const MAX_DOCUMENT_PAYLOAD_BYTES = 4 * 1024 * 1024;
const SMART_FILL_SEMANTIC_KEY_PATTERN = /^(?:unknown|[a-z][a-z0-9_]*(?:\.[a-z0-9_]+)+)$/;

export class BridgeValidationError extends Error {
  readonly path: string;

  constructor(path: string, message: string) {
    super(`${path}: ${message}`);
    this.name = 'BridgeValidationError';
    this.path = path;
  }
}

export function fail(path: string, message: string): never {
  throw new BridgeValidationError(path, message);
}

function isPlainObject(value: unknown): value is UnknownRecord {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

export function objectAt(value: unknown, path: string): UnknownRecord {
  if (!isPlainObject(value)) fail(path, 'expected a plain JSON object');
  return value;
}

export function hasOwn(value: UnknownRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(value, key);
}

export function exactObject(
  value: unknown,
  path: string,
  requiredKeys: readonly string[],
  optionalKeys: readonly string[] = []
): UnknownRecord {
  const object = objectAt(value, path);
  const allowed = new Set([...requiredKeys, ...optionalKeys]);

  for (const key of Object.keys(object)) {
    if (!allowed.has(key)) fail(`${path}.${key}`, 'unknown property');
  }
  for (const key of requiredKeys) {
    if (!hasOwn(object, key)) fail(`${path}.${key}`, 'missing required property');
  }
  return object;
}

export function textAt(value: unknown, path: string, maxLength = MAX_SHORT_TEXT): string {
  if (typeof value !== 'string') fail(path, 'expected a string');
  if (value.length > maxLength) fail(path, `must be at most ${maxLength} characters`);
  return value;
}

export function nonBlankTextAt(value: unknown, path: string, maxLength = MAX_SHORT_TEXT): string {
  const text = textAt(value, path, maxLength);
  if (text.trim().length === 0) fail(path, 'must not be blank');
  return text;
}

export function bucketNameAt(value: unknown, path: string): string {
  if (typeof value !== 'string') fail(path, 'expected a string');
  if (value.trim().length === 0) fail(path, 'must not be blank');
  if (Array.from(value).length > MAX_BUCKET_NAME_LENGTH) {
    fail(path, `must be at most ${MAX_BUCKET_NAME_LENGTH} characters`);
  }
  return value;
}

export function isSmartFillSemanticKey(value: string): boolean {
  return SMART_FILL_SEMANTIC_KEY_PATTERN.test(value);
}

export function semanticKeyAt(value: unknown, path: string): string {
  const semanticKey = nonBlankTextAt(value, path);
  if (!isSmartFillSemanticKey(semanticKey)) fail(path, 'expected a valid semantic key');
  return semanticKey;
}

export function identifierAt(value: unknown, path: string): string {
  if (!isBridgeIdentifier(value)) {
    fail(
      path,
      'expected a 1-128 character identifier using letters, numbers, ".", "_", ":", or "-"'
    );
  }
  return value;
}

export function requestIdAt(value: unknown, path: string): BridgeRequestId {
  return asBridgeRequestId(identifierAt(value, path));
}

export function booleanAt(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') fail(path, 'expected a boolean');
  return value;
}

export function integerAt(value: unknown, path: string, max = Number.MAX_SAFE_INTEGER): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0 || (value as number) > max) {
    fail(path, `expected an integer from 0 to ${max}`);
  }
  return value as number;
}

export function numberAt(value: unknown, path: string, minimum: number, maximum: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < minimum || value > maximum) {
    fail(path, `expected a finite number from ${minimum} to ${maximum}`);
  }
  return value;
}

export function literalAt<const T extends string | number | boolean | null>(
  value: unknown,
  expected: T,
  path: string
): T {
  if (value !== expected) fail(path, `expected ${JSON.stringify(expected)}`);
  return expected;
}

export function enumAt<const T extends readonly string[]>(
  value: unknown,
  allowed: T,
  path: string
): T[number] {
  if (typeof value !== 'string' || !(allowed as readonly string[]).includes(value)) {
    fail(path, `expected one of ${allowed.join(', ')}`);
  }
  return value as T[number];
}

/**
 * A property or review value. The cap is in UTF-8 bytes because that is how
 * the server measures it; a character cap let 100 000 two-byte characters
 * through the bridge only for the server to refuse them after the save had
 * already locked its operations.
 */
export function valueTextAt(value: unknown, path: string): string {
  const parsed = textAt(value, path, MAX_DOCUMENT_VALUE_BYTES);
  if (new TextEncoder().encode(parsed).byteLength > MAX_DOCUMENT_VALUE_BYTES) {
    fail(path, `must be at most ${MAX_DOCUMENT_VALUE_BYTES} bytes`);
  }
  return parsed;
}

export function nullableAt<T>(value: unknown, path: string, parser: Parser<T>): T | null {
  return value === null ? null : parser(value, path);
}

export function arrayAt<T>(
  value: unknown,
  path: string,
  parser: Parser<T>,
  maxLength = MAX_COLLECTION_SIZE
): T[] {
  if (!Array.isArray(value)) fail(path, 'expected an array');
  if (value.length > maxLength) fail(path, `must contain at most ${maxLength} items`);
  return value.map((item, index) => parser(item, `${path}[${index}]`));
}

/**
 * Builds the exact-shape parser almost every DTO uses: unknown keys are
 * rejected, each declared field runs its validator at `${path}.${key}` in
 * declaration order (optional fields validate only when present), refine
 * runs cross-field rules last, and the SAME reference returns — parsers
 * never clone.
 */
export function objectParser<T>(
  fields: Record<string, Parser<unknown>>,
  options: {
    optional?: Record<string, Parser<unknown>>;
    refine?: (object: UnknownRecord, path: string) => void;
  } = {}
): (value: unknown, path?: string) => T {
  const requiredKeys = Object.keys(fields);
  const optionalFields = options.optional ?? {};
  const optionalKeys = Object.keys(optionalFields);
  return (value, path = '$') => {
    const object = exactObject(value, path, requiredKeys, optionalKeys);
    for (const key of requiredKeys) fields[key]!(object[key], `${path}.${key}`);
    for (const key of optionalKeys) {
      if (hasOwn(object, key)) optionalFields[key]!(object[key], `${path}.${key}`);
    }
    options.refine?.(object, path);
    return value as T;
  };
}

// Declarative field rules for objectParser tables.
export const nullable =
  (parser: Parser<unknown>): Parser<unknown> =>
  (value, path) =>
    nullableAt(value, path, parser);
export const oneOf =
  (allowed: readonly string[]): Parser<unknown> =>
  (value, path) =>
    enumAt(value, allowed, path);
export const text =
  (maxLength: number): Parser<unknown> =>
  (value, path) =>
    textAt(value, path, maxLength);
export const nonBlankText =
  (maxLength: number): Parser<unknown> =>
  (value, path) =>
    nonBlankTextAt(value, path, maxLength);
export const literal =
  (expected: string | number | boolean | null): Parser<unknown> =>
  (value, path) =>
    literalAt(value, expected, path);
export const arrayOf =
  (parser: Parser<unknown>, maxLength?: number): Parser<unknown> =>
  (value, path) =>
    arrayAt(value, path, parser, maxLength);
export const boundedIntegerAt =
  (max: number): Parser<number> =>
  (value, path) =>
    integerAt(value, path, max);

export function positiveIntegerAt(value: unknown, path: string): number {
  const parsed = integerAt(value, path);
  if (parsed < 1) fail(path, 'must be positive');
  return parsed;
}

export function confidenceAt(value: unknown, path: string): number {
  return numberAt(value, path, 0, 1);
}

export function assertUnique<T>(
  values: readonly T[],
  key: (value: T) => string,
  path: string,
  label: string
): void {
  const seen = new Set<string>();
  for (const value of values) {
    const id = key(value);
    if (seen.has(id)) fail(path, `contains duplicate ${label} ${JSON.stringify(id)}`);
    seen.add(id);
  }
}

export function assertJsonByteSize(value: unknown, path: string, maxBytes: number): void {
  let serialized: string;
  try {
    serialized = JSON.stringify(value);
  } catch {
    fail(path, 'must be JSON serializable');
  }
  if (new TextEncoder().encode(serialized).byteLength > maxBytes) {
    fail(path, `must be at most ${maxBytes} encoded bytes`);
  }
}

export function timestampAt(value: unknown, path: string): string {
  const timestamp = textAt(value, path, 64);
  if (
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/.test(timestamp) ||
    !Number.isFinite(Date.parse(timestamp))
  ) {
    fail(path, 'expected an ISO 8601 timestamp with a timezone');
  }
  return timestamp;
}

export function webOriginAt(value: unknown, path: string): string {
  const origin = textAt(value, path, 2_048);
  let parsed: URL;
  try {
    parsed = new URL(origin);
  } catch {
    fail(path, 'expected an http(s) origin');
  }
  if (
    (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') ||
    parsed.origin !== origin ||
    parsed.pathname !== '/' ||
    parsed.search !== '' ||
    parsed.hash !== ''
  ) {
    fail(path, 'expected an http(s) origin without a path, query, or fragment');
  }
  return origin;
}

export function bucketOriginAt(value: unknown, path: string): SmartFillBucketOrigin {
  return enumAt(value, SMART_FILL_BUCKET_ORIGINS, path);
}

export function visibilityAt(value: unknown, path: string): SmartFillBucketVisibility {
  return enumAt(value, SMART_FILL_BUCKET_VISIBILITIES, path);
}
